import { useEffect, useState, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Search, ShoppingCart, Store, MapPin, Plus, Sparkles, Truck } from "lucide-react";
import { grocery, addToCart, cartTotals, formatXAF } from "../../lib/grocery";
import { toast } from "sonner";

export default function GroceryHomePage() {
  const navigate = useNavigate();
  const [search, setSearch] = useSearchParams();
  const [categories, setCategories] = useState([]);
  const [shops, setShops] = useState([]);
  const [quickBuy, setQuickBuy] = useState([]);
  const [zones, setZones] = useState([]);
  const [query, setQuery] = useState(search.get("q") || "");
  const [results, setResults] = useState([]);
  const [cart, setCart] = useState(cartTotals());
  const activeCategory = search.get("cat") || "";

  useEffect(() => {
    grocery.categories().then((d) => setCategories(d.categories || []));
    grocery.shops().then(setShops).catch(() => setShops([]));
    grocery.zones().then((d) => setZones(d.zones || []));
    grocery.products({ quick_buy: true, limit: 12 }).then(setQuickBuy).catch(() => setQuickBuy([]));
  }, []);

  useEffect(() => {
    const refresh = () => setCart(cartTotals());
    window.addEventListener("kanama-cart-changed", refresh);
    return () => window.removeEventListener("kanama-cart-changed", refresh);
  }, []);

  useEffect(() => {
    if (!query.trim() && !activeCategory) {
      setResults([]);
      return;
    }
    const params = { limit: 40 };
    if (query.trim()) params.q = query.trim();
    if (activeCategory) params.category = activeCategory;
    grocery.products(params).then(setResults).catch(() => setResults([]));
  }, [query, activeCategory]);

  const shopsByZone = useMemo(() => {
    const map = {};
    shops.forEach((s) => {
      map[s.zone_slug] = map[s.zone_slug] || [];
      map[s.zone_slug].push(s);
    });
    return map;
  }, [shops]);

  const handleAdd = (p) => {
    addToCart(p, 1);
    toast.success(`${p.name} ajouté au panier`);
  };

  const onCategoryClick = (key) => {
    const params = new URLSearchParams(search);
    if (activeCategory === key) params.delete("cat");
    else params.set("cat", key);
    setSearch(params);
  };

  return (
    <div className="min-h-[80vh] bg-brand-paper" data-testid="grocery-home">
      {/* Hero */}
      <section className="relative bg-brand-ink text-white overflow-hidden">
        <div className="absolute inset-0 opacity-25 bg-gradient-to-br from-brand-gold/40 via-transparent to-brand-terracotta/30" />
        <div className="relative max-w-6xl mx-auto px-5 sm:px-8 py-10 sm:py-16">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-brand-gold/20 text-brand-gold text-xs uppercase tracking-[0.22em] font-bold mb-4">
            <Sparkles className="w-3.5 h-3.5" />
            Nouveau · N'Djamena
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight mb-3">
            Kanama Courses
          </h1>
          <p className="text-base sm:text-lg text-white/85 max-w-2xl mb-6 leading-relaxed">
            Viande, poisson, fruits, riz, boissons, hygiène… Vos courses livrées à <strong className="text-brand-gold">Chagoua, Moursal, Gassi, Klemat, Farcha, Atrone</strong>. Paiement <strong className="text-brand-gold">Airtel, Moov ou Cash</strong>.
          </p>

          <div className="flex items-center gap-2 bg-white rounded-2xl p-2 max-w-xl shadow-xl">
            <Search className="w-5 h-5 text-brand-inkSoft ml-2 shrink-0" />
            <Input
              data-testid="grocery-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Riz, tomates, poulet, baguette…"
              className="border-0 shadow-none focus-visible:ring-0 text-brand-ink h-11"
            />
            <Button
              data-testid="grocery-go-cart"
              onClick={() => navigate("/courses/panier")}
              className="rounded-xl bg-brand-gold hover:bg-brand-goldDark text-brand-black font-semibold relative h-11"
            >
              <ShoppingCart className="w-4 h-4 mr-1.5" />
              Panier
              {cart.count > 0 && (
                <span className="absolute -top-2 -right-2 bg-brand-terracotta text-white text-[10px] font-bold rounded-full px-1.5 min-w-[20px] h-5 flex items-center justify-center" data-testid="grocery-cart-badge">
                  {cart.count}
                </span>
              )}
            </Button>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="max-w-6xl mx-auto px-4 sm:px-8 py-8">
        <h2 className="font-heading text-xl sm:text-2xl font-semibold text-brand-ink mb-4">
          Catégories
        </h2>
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-9 gap-2 sm:gap-3">
          {categories.map((c) => {
            const active = activeCategory === c.key;
            return (
              <button
                key={c.key}
                onClick={() => onCategoryClick(c.key)}
                data-testid={`grocery-cat-${c.key}`}
                className={`flex flex-col items-center gap-1.5 p-3 rounded-2xl border transition-all touch-manipulation ${
                  active
                    ? "bg-brand-ink text-brand-gold border-brand-ink shadow-lg scale-105"
                    : "bg-white border-brand-line hover:border-brand-gold text-brand-ink"
                }`}
              >
                <span className="text-2xl sm:text-3xl">{c.icon}</span>
                <span className="text-[11px] sm:text-xs font-semibold text-center leading-tight">{c.label}</span>
                {c.fresh && (
                  <span className="text-[9px] uppercase tracking-wider text-brand-terracotta font-bold">Frais</span>
                )}
              </button>
            );
          })}
        </div>
      </section>

      {/* Search results */}
      {(query.trim() || activeCategory) && (
        <section className="max-w-6xl mx-auto px-4 sm:px-8 pb-8" data-testid="grocery-results-section">
          <h2 className="font-heading text-xl font-semibold text-brand-ink mb-4">
            {results.length} résultat{results.length > 1 ? "s" : ""}
            {activeCategory ? ` · ${categories.find((c) => c.key === activeCategory)?.label || ""}` : ""}
          </h2>
          {results.length === 0 ? (
            <p className="text-brand-inkSoft">Aucun produit pour cette recherche.</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {results.map((p) => (
                <ProductCard key={p.id} product={p} onAdd={handleAdd} />
              ))}
            </div>
          )}
        </section>
      )}

      {/* Quick-buy */}
      {!query.trim() && !activeCategory && quickBuy.length > 0 && (
        <section className="max-w-6xl mx-auto px-4 sm:px-8 pb-8" data-testid="grocery-quickbuy-section">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-xl sm:text-2xl font-semibold text-brand-ink">
              ⚡ Achats rapides
            </h2>
            <span className="text-xs text-brand-inkSoft uppercase tracking-wider">Les plus commandés</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {quickBuy.map((p) => (
              <ProductCard key={p.id} product={p} onAdd={handleAdd} />
            ))}
          </div>
        </section>
      )}

      {/* Shops list */}
      {!query.trim() && !activeCategory && (
        <section className="max-w-6xl mx-auto px-4 sm:px-8 pb-12" data-testid="grocery-shops-section">
          <h2 className="font-heading text-xl sm:text-2xl font-semibold text-brand-ink mb-4">
            Nos boutiques partenaires
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {shops.map((s) => (
              <Link
                key={s.id}
                to={`/courses/shop/${s.id}`}
                data-testid={`grocery-shop-card-${s.id}`}
                className="block"
              >
                <Card className="p-5 rounded-2xl border-brand-line hover:border-brand-gold transition-all hover:shadow-lg h-full">
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-12 h-12 rounded-xl bg-brand-sandlight flex items-center justify-center text-brand-gold shrink-0">
                      <Store className="w-6 h-6" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-heading font-semibold text-brand-ink truncate">{s.name}</h3>
                      <div className="flex items-center gap-1 text-xs text-brand-inkSoft">
                        <MapPin className="w-3 h-3" />
                        {zones.find((z) => z.slug === s.zone_slug)?.name || s.zone_slug}
                      </div>
                    </div>
                    {s.open && <Badge className="bg-emerald-600 hover:bg-emerald-700 text-white">Ouvert</Badge>}
                  </div>
                  <p className="text-sm text-brand-inkSoft leading-relaxed line-clamp-2">{s.description}</p>
                </Card>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Zones banner */}
      <section className="max-w-6xl mx-auto px-4 sm:px-8 pb-16">
        <Card className="p-5 sm:p-6 rounded-2xl border-brand-line bg-gradient-to-br from-brand-sandlight to-white">
          <div className="flex items-start gap-3 mb-3">
            <Truck className="w-6 h-6 text-brand-gold mt-1 shrink-0" />
            <div>
              <h3 className="font-heading font-semibold text-brand-ink text-lg">
                Livraison N'Djamena
              </h3>
              <p className="text-sm text-brand-inkSoft mt-1">
                Frais selon votre quartier. Option Express +500 FCFA pour livraison en moins de 60 min.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 mt-3">
            {zones.map((z) => (
              <span
                key={z.slug}
                className="inline-flex items-center gap-1.5 bg-white border border-brand-line rounded-full px-3 py-1.5 text-xs font-medium text-brand-ink"
                data-testid={`grocery-zone-chip-${z.slug}`}
              >
                <MapPin className="w-3 h-3 text-brand-gold" />
                {z.name}
                <span className="text-brand-goldDark font-bold">{formatXAF(z.delivery_fee)}</span>
              </span>
            ))}
          </div>
        </Card>
      </section>

      {/* Floating cart bar */}
      {cart.count > 0 && (
        <div
          className="md:hidden fixed left-3 right-3 z-40 animate-in slide-in-from-bottom"
          style={{ bottom: "calc(72px + env(safe-area-inset-bottom, 0))" }}
          data-testid="grocery-floating-cart"
        >
          <button
            onClick={() => navigate("/courses/panier")}
            className="w-full bg-brand-ink text-white rounded-2xl px-4 py-3 flex items-center justify-between shadow-2xl active:scale-[0.98] transition-transform"
          >
            <span className="flex items-center gap-2">
              <span className="bg-brand-gold text-brand-black rounded-full w-7 h-7 flex items-center justify-center text-sm font-bold">
                {cart.count}
              </span>
              <span className="font-semibold">Voir mon panier</span>
            </span>
            <span className="text-brand-gold font-bold">{formatXAF(cart.subtotal_xaf)}</span>
          </button>
        </div>
      )}
    </div>
  );
}

function ProductCard({ product, onAdd }) {
  return (
    <Card
      className="p-3 rounded-2xl border-brand-line hover:border-brand-gold transition-all hover:shadow-md flex flex-col"
      data-testid={`grocery-product-${product.id}`}
    >
      <div className="aspect-square rounded-xl bg-brand-sandlight flex items-center justify-center mb-2 overflow-hidden">
        {product.image_url ? (
          <img src={product.image_url} alt={product.name} className="w-full h-full object-cover" />
        ) : (
          <span className="text-4xl opacity-70">🛒</span>
        )}
      </div>
      <h3 className="font-semibold text-brand-ink text-sm leading-tight line-clamp-2 mb-1">{product.name}</h3>
      <div className="text-[11px] text-brand-inkSoft uppercase tracking-wider mb-2">par {product.unit}</div>
      <div className="flex items-center justify-between mt-auto gap-2">
        <span className="font-heading font-bold text-brand-goldDark text-base whitespace-nowrap">
          {formatXAF(product.price_xaf)}
        </span>
        <Button
          size="sm"
          onClick={() => onAdd(product)}
          className="h-8 px-2 rounded-lg bg-brand-ink hover:bg-brand-inkSoft text-white"
          data-testid={`grocery-add-${product.id}`}
        >
          <Plus className="w-4 h-4" />
        </Button>
      </div>
    </Card>
  );
}
